import React, { useRef } from 'react';
import { Button } from '@/components/Button';
import { Dialog } from './Dialog';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: React.ReactNode;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` styles the confirm button destructively. Default `danger`. */
  tone?: 'danger' | 'primary';
  /** Disables buttons + shows a spinner on confirm while the action runs. */
  loading?: boolean;
}

/**
 * Confirmation dialog built on Dialog. Defaults focus to the safe Cancel
 * action; the confirm button carries the destructive tone for cancellations.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  loading = false,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog
      open={open}
      onClose={loading ? () => undefined : onClose}
      title={title}
      size="sm"
      closeOnBackdrop={!loading}
      initialFocusRef={cancelRef}
      icon={
        tone === 'danger' ? (
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-error-lighter text-error-base">
            <i className="ri-error-warning-line text-lg" />
          </span>
        ) : undefined
      }
      footer={
        <>
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={loading}
            className="inline-flex min-h-11 items-center justify-center rounded-md border border-stroke-sub-300 bg-bg-white-0 px-4 text-sm font-medium text-text-sub-600 transition-colors hover:bg-bg-weak-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={loading}
            className="min-h-11"
          >
            {loading && (
              <i className="ri-loader-4-line mr-1.5 animate-spin" />
            )}
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-text-sub-600">{message}</p>
    </Dialog>
  );
};

export default ConfirmDialog;
